import { PRESENT_VALUE_SENTINEL } from '../../../types/QueryBuilder';
import type { Operator, ParsedToken } from '../../../types/QueryBuilder';

// =============================================================================
// Lezer (QueryBuilder) → ANTLR (Observability Search) query converter
//
// Mirrors `buildObsSearchPayload` in QueryBuilder.stories.tsx so the production
// metric form and the stories playground emit identical ANTLR for the same
// parsed query. Anything that changes here should change there too.
// =============================================================================

const OBS_NEEDS_QUOTE_RE = /[\s"'`:=><*]/;

function quoteAntlrValue(value: string): string {
  // Force-quote an empty value: bare `field=` is malformed ANTLR; `field=""` round-trips.
  if (value !== '' && !OBS_NEEDS_QUOTE_RE.test(value)) {
    return value;
  }
  return `"${value.replace(/"/g, '\\"')}"`;
}

// Negation flips the operator rather than wrapping with `NOT`. Operators not
// listed here (`>`, `<`, `>=`, `<=`, `**`, `^=`, `=^`) have no
// negated form in this mapping and the negation is dropped — matches stories.
const OBS_NEGATED_OP: Partial<Record<Operator, Operator>> = {
  '=': '!=',
  '!=': '=',
  '**': '!**',
  '!**': '**',
  '//': '!//',
  '!//': '//',
};

// Lezer EXPRESSION.expressionType (camelCase) → ANTLR relational prefix (snake_case).
// Unknown types emit a plain `(...)` group.
const OBS_RELATIONAL_PREFIX: Record<string, string> = {
  anySpan: 'any_span',
  anySpan2: 'any_span2',
  anySpan3: 'any_span3',
  noSpan: 'no_span',
  childSpan: 'child_span',
  parentSpan: 'parent_span',
  rootSpan: 'root_span',
  anySession: 'any_session',
};

function filterTokenToAntlr(token: Extract<ParsedToken, { type: 'FILTER' }>): string {
  const { field, operator, value, negated } = token;
  const scalars = (Array.isArray(value) ? value : [value]).map(String);

  // PRESENT_VALUE_SENTINEL → EXISTS / NOT EXISTS. Either the `!` negation prefix
  // or the `!=` operator can flip the semantic — XOR them so e.g. `field:!=:present`
  // produces `field NOT EXISTS` and `!field:!=:present` produces `field EXISTS`.
  const isPresent =
    value === PRESENT_VALUE_SENTINEL ||
    (Array.isArray(value) && value.length === 1 && value[0] === PRESENT_VALUE_SENTINEL);
  if (isPresent) {
    const isNotExists = !!negated !== (operator === '!=');
    return `${field} ${isNotExists ? 'NOT EXISTS' : 'EXISTS'}`;
  }

  const effectiveOp: Operator = negated ? (OBS_NEGATED_OP[operator] ?? operator) : operator;

  // Multi-value filters expand to a boolean group: positive matchers join with OR,
  // negated with AND. A single value emits a bare term. Every multi-value operator
  // must use this helper or the extra values are silently dropped.
  const group = (join: 'OR' | 'AND', term: (v: string) => string): string =>
    scalars.length > 1 ? `(${scalars.map(term).join(` ${join} `)})` : term(scalars[0]);

  if (effectiveOp === '=') {
    return group('OR', (v) => `${field}=${quoteAntlrValue(v)}`);
  }
  if (effectiveOp === '!=') {
    return group('AND', (v) => `${field}!=${quoteAntlrValue(v)}`);
  }
  if (effectiveOp === '^=') {
    return group('OR', (v) => `${field}=${quoteAntlrValue(`${v}*`)}`);
  }
  if (effectiveOp === '=^') {
    return group('OR', (v) => `${field}=${quoteAntlrValue(`*${v}`)}`);
  }
  if (effectiveOp === '**') {
    return group('OR', (v) => `${field}=${quoteAntlrValue(`*${v}*`)}`);
  }
  if (effectiveOp === '!**') {
    // "does not contain" is a negated glob; multi-value means none may match (AND).
    return group('AND', (v) => `${field}!=${quoteAntlrValue(`*${v}*`)}`);
  }
  if (effectiveOp === '//' || effectiveOp === '!//') {
    // Regex value already carries its `/` delimiters; force-quote and escape `"`.
    // `!//` ("does not match") is negated, so multi-value combines with AND.
    const eq = effectiveOp === '//' ? '=' : '!=';
    const join = effectiveOp === '//' ? 'OR' : 'AND';
    return group(join, (v) => `${field}${eq}"${v.replace(/"/g, '\\"')}"`);
  }
  // Comparison operators (>, >=, <, <=) are single-value: only offered on non-multi numeric fields.
  return `${field}${effectiveOp}${quoteAntlrValue(scalars[0])}`;
}

export function parsedQueryToAntlr(tokens: ParsedToken[]): string {
  return tokens
    .map((token) => {
      switch (token.type) {
        case 'FILTER':
          return filterTokenToAntlr(token);
        case 'LOGIC_BOOL':
          return token.value;
        case 'EXPRESSION': {
          const prefix = token.expressionType ? (OBS_RELATIONAL_PREFIX[token.expressionType] ?? '') : '';
          return `${prefix}(${parsedQueryToAntlr(token.inner)})`;
        }
        case 'FREE_TEXT':
          // No ANTLR key/value equivalent; skip.
          return '';
        default: {
          const _exhaustive: never = token;
          return '';
        }
      }
    })
    .filter(Boolean)
    .join(' ');
}
